import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ApplicationDetails } from '@app/app/interfaces/application-details.model';
import { Observable, Subject } from 'rxjs';
import { ApplicationDetailsState } from '@app/app/store/application-details/application-details.state';
import { Select, Store } from '@ngxs/store';
import { PortalUser } from '@app/app/interfaces/portal-user.model';
import { PortalUsersState } from '@app/app/store/portal-users/portal-users-state.service';
import { takeUntil } from 'rxjs/operators';
import { Offer } from '@app/app/interfaces/offer.model';
import { DeclineDialogComponent } from '@app/app/components/decline-button/decline-dialog/decline-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { Approval } from '@app/app/interfaces/approval/approval.model';
import { ApprovalsState } from '@app/app/store/offers/approvals.state';
import { sortBy } from 'lodash';
import { SaasFeaturesState } from '@app/app/store/saas-features/saas-features.state';
import { ConfettiService } from '@app/app/services/confetti.service';
import { ChangeDealStatus } from '@app/app/store/application-details/application-details.actions';
import { AuthUser } from '@app/app/store/auth/auth-user';
import { AuthState } from '@app/app/store/auth/auth.state';
import { EsignatureState } from '@app/app/store/esignature/esignature.state';
import { Esigner } from '@app/app/interfaces/esigner.model';
import { GetStatusesForSigners } from '@app/app/store/esignature/esignature.actions';

@Component( {
  selector: 'app-deal-sidebar',
  templateUrl: './deal-sidebar.component.html',
  styleUrls: [ './deal-sidebar.component.scss' ]
} )
export class DealSidebarComponent implements OnInit, OnDestroy {

  @Select( ApplicationDetailsState.applicationDetails ) applicationDetails$: Observable<ApplicationDetails>;
  @Select( ApprovalsState.approvals ) approvals$: Observable<Approval[]>;
  @Select(AuthState.user) currentUser$: Observable<AuthUser>;
  @Select( PortalUsersState.portalUsers ) portalUsers$: Observable<PortalUser[]>;
  @Select(SaasFeaturesState.saasPermitted('funnelFundingDesk')) hasFundingDeskFeature$: Observable<boolean>;
  @Select(SaasFeaturesState.saasPermitted('instantContracts')) hasInstantContractsFeature$: Observable<boolean>;
  @Select(EsignatureState.signingStatuses) signers$: Observable<Esigner[]>;
  @Select(SaasFeaturesState.saasPermitted('manualStatusChanges', 'lpxManuallyChangeDealStatus.write')) permittedToManuallyChangeStatus$: Observable<boolean>;

  @Input() dealId: number;

  @ViewChild('fundedButton', { read: ElementRef<HTMLElement> }) fundedButton: ElementRef<HTMLElement>;

  destroyed$ = new Subject<boolean>();

  assignmentsArray: Array<number> = [];
  isStatusContractIn: boolean;
  mostRecentOffer: Offer;
  mostRecentApproval: Approval;
  offerAccepted?: string;

  constructor(
    protected _confettiService: ConfettiService,
    private dialog: MatDialog,
    private store: Store,
  ) {
  }

  ngOnInit(): void {
    this.store.dispatch(new GetStatusesForSigners(this.dealId));
    this.approvals$.pipe(
      takeUntil(this.destroyed$)
    ).subscribe( approvals => {
      if(approvals?.length) {
        const dealApprovals = approvals.filter(approval => approval.dealId === this.dealId);
        this.mostRecentApproval = sortBy(dealApprovals, 'created')[0];
      }
    });

    this.applicationDetails$.pipe(
      takeUntil( this.destroyed$ )
    ).subscribe( applicationDetails => {
      this.assignmentsArray = JSON.parse( applicationDetails?.deal?.assignments );
      this.mostRecentOffer = applicationDetails?.deal?.offers_desc[0];
      this.isStatusContractIn = this._isStatusContractIn(applicationDetails);
      this.mostRecentOffer = applicationDetails?.deal?.offers_desc[0] || null;
    });
  }

  ngOnDestroy() {
    this.destroyed$.next( true );
    this.destroyed$.complete();
  }

  openDeclineDialog(): void {
    this.dialog.open(DeclineDialogComponent, {
      width: '450px',
      data: { dealId: this.dealId },
    });
  }

  changeDealStatus(status: string, fund: boolean = false): void {
    this.store.dispatch(new ChangeDealStatus(this.dealId, status, fund));
    if(status == 'funding' && fund){
      this._confettiService.confetti(this.fundedButton.nativeElement);
    }
  }

  private _isStatusContractIn(applicationDetails: ApplicationDetails): boolean {
    return (
      applicationDetails.deal.stage === 'closing' &&
      applicationDetails.deal.status === 'contractIn'
    );
  }

}
